import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { AuthCard } from "@/pages/SignupPage";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title="Reset your password" subtitle="We'll email you a link to choose a new one">
      {sent ? (
        <p className="text-sm" data-testid="forgot-sent">If an account exists for <b>{email}</b>, a reset link is on its way. It works once and expires in 1 hour. Check your spam folder if it doesn't arrive.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-1.5"><Label htmlFor="femail">Email</Label><Input id="femail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" /></div>
          {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
          <Button type="submit" className="w-full h-11" disabled={busy} data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
      <p className="text-xs mt-5"><Link to="/login" className="text-primary hover:underline" data-testid="forgot-back-link">Back to sign in</Link></p>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const token = params.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      setError("The passwords don't match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password changed. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title="Choose a new password" subtitle="At least 8 characters">
      {!token ? <p className="text-sm text-destructive">This reset link is incomplete. Please request a new one.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div className="space-y-1.5"><Label htmlFor="npw">New password</Label><Input id="npw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="reset-password-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="npw2">Confirm new password</Label><Input id="npw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="reset-password-confirm-input" /></div>
          {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
          <Button type="submit" className="w-full h-11" disabled={busy} data-testid="reset-submit-button">{busy ? "Saving…" : "Save new password"}</Button>
        </form>
      )}
      <p className="text-xs mt-5"><Link to="/forgot-password" className="text-primary hover:underline">Request a new link</Link></p>
    </AuthCard>
  );
}
